import React from "react";
import PropTypes from "prop-types";
import { useHistory, useParams } from "react-router-dom";
import { ChevronLeft, History } from "react-ikonate";
import toast, { Toaster } from "react-hot-toast";
import * as api from "shared/api/products";
import * as mediaApi from "shared/api/media";
import { errorMessage } from "shared/api/client";
import {
  attributes as attributesApi,
  autosaves as autosavesApi,
  versions as versionsApi,
} from "shared/api";
import Button, { ButtonPurpose } from "shared/Button";
import { Modal } from "shared/Modal";
import ContentSettingsCard from "./components/ContentSettingsCard";
import PricingCard from "./components/PricingCard";
import BundlePricingCard from "./components/BundlePricingCard";
import ProductImageCard from "./components/ProductImageCard";
import CollapsibleSection from "./components/CollapsibleSection";
import NetSuiteVariationsCard from "./components/NetSuiteVariationsCard";
import StoreVariationsCard from "./components/StoreVariationsCard";
import VersionPicker from "./components/VersionPicker";
import ThumbnailCard from "./components/ThumbnailCard";
import HistoryOverlay from "./components/HistoryOverlay";
import styles from "./ProductEditor.module.css";

const VersionDetailsCard = ({ currentVersion, onChange, onDirtyChange }) => {
  const [name, setName] = React.useState(
    currentVersion ? currentVersion.name : ""
  );
  const [desc, setDesc] = React.useState(
    currentVersion ? currentVersion.description || "" : ""
  );

  const nameChanged = name !== (currentVersion?.name || "");
  const descChanged = desc !== (currentVersion?.description || "");
  const dirty = nameChanged || descChanged;

  React.useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  // Report the current name/description up so the main Save can persist them
  // alongside the rest of the form — there's
  // no separate save for version details anymore.
  React.useEffect(() => {
    if (onChange) onChange(name, desc);
  }, [name, desc, onChange]);

  // Clear the lifted dirty flag when this card unmounts (e.g. switching
  // versions remounts via key) so a stale "unsaved version details" warning
  // never lingers.
  React.useEffect(
    () => () => {
      if (onDirtyChange) onDirtyChange(false);
    },
    [onDirtyChange]
  );

  return (
    <CollapsibleSection
      title="Version Details"
      className={styles.versionCard}
      bodyClassName={styles.cardBody}
    >
      <div className={styles.versionFields}>
        <div className={styles.versionField}>
          <label className={styles.label} htmlFor="pe-version-name">
            Version Name
          </label>
          <input
            id="pe-version-name"
            className={styles.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Version name"
          />
        </div>
        <div className={styles.versionField}>
          <label className={styles.label} htmlFor="pe-version-description">
            Description
          </label>
          <textarea
            id="pe-version-description"
            className={styles.textarea}
            rows={2}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="N/A"
          />
        </div>
      </div>
    </CollapsibleSection>
  );
};

const membershipOptions = [
  { id: "C", label: "C" },
  { id: "P", label: "P" },
];

const pinLevels = [
  { id: "IBO", label: "IBO" },
  { id: "PLA", label: "PLA" },
  { id: "EME", label: "EME" },
  { id: "DIA", label: "DIA" },
];

// Everything selected is how the editor says "everyone", which is what an
// unrestricted product means, so that is the state a new product starts in.
export const DEFAULT_MEMBERSHIPS = membershipOptions.map((o) => o.id);
export const DEFAULT_PINS = pinLevels.map((o) => o.id);

// A stored product can only have an empty code list if it was never set --
// audienceSelectionError() refuses to save one -- and the storefront already
// reads an empty list as "no restriction". Rendering that as every badge off
// was therefore showing the opposite of what the record means, so an empty
// list falls back to the all-selected default instead of clearing it.
export function audienceCodesOrDefault(codes, fallback) {
  return Array.isArray(codes) && codes.length ? codes : fallback;
}

// "Draft" was retired as a publish status — autosave and version history
// already cover work in progress, and "In Review" is the not-live state. A
// handful of products saved before that still carry it, so map them forward
// rather than leaving the status pill blank on an old record.
export function normalizePublishStatus(status) {
  return status === "draft" ? "in_review" : status;
}

const PUBLISH_STATUSES = [
  {
    key: "in_review",
    label: "In Review",
    activeClass: "statusInReviewActive",
    baseClass: "statusInReview",
  },
  {
    key: "scheduled",
    label: "Scheduled",
    activeClass: "statusScheduledActive",
    baseClass: "statusScheduled",
  },
  {
    key: "published",
    label: "Published",
    activeClass: "statusPublishedActive",
    baseClass: "statusPublished",
  },
  {
    key: "archived",
    label: "Archived",
    activeClass: "statusArchivedActive",
    baseClass: "statusArchived",
  },
];

// ----- Publish-On scheduling helpers -----
// The Publish On date/time inputs drive automatic status transitions: a future
// date parks the product in "Scheduled"; when the moment arrives it flips to
// "Published". These helpers convert between the <input type="date/time"> string
// format (local time) and a real Date so the comparison uses the user's clock.
const parsePublishAt = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T${timeStr || "00:00"}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDateInputValue = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toTimeInputValue = (d) => {
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${min}`;
};

// ----- Version persistence (pre-Mongo stopgap) -----
// Versions are a NEW COPY of a product (item-12). There is no versions endpoint
// on the BFF yet (that's a Mongo seam), so we persist the whole version list —
// INCLUDING each version's full content snapshot — to localStorage, keyed by
// product id. Each version is self-contained: its `content` is the single
// source of truth for that version and is never re-derived from the server
// (the server only seeds the very first "primary" version). The BFF's single
// product record simply mirrors whichever version is currently live. Swap this
// for a real products/:id/versions endpoint once Mongo is wired.
//
// Key is suffixed v2 because the previous shape stored disjoint baselines that
// could corrupt "primary"; bumping it discards any stale/corrupt local data.
const versionsStorageKey = (productId) => `wwg_store_versions_v2_${productId}`;

// JSON with sorted keys, so two equal version lists always compare equal.
const stableJson = (value) =>
  JSON.stringify(value, (_key, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.keys(v)
          .sort()
          .reduce((o, k) => {
            o[k] = v[k];
            return o;
          }, {})
      : v
  );

// The BFF saves to Mongo first, then mirrors the product into Store.Products so
// the member storefront (which reads SQL, never Mongo) can see it. That mirror
// can legitimately fail — a product with no NetSuite item or no price cannot be
// represented in SQL — and the save still succeeds. Saying only "Product saved"
// in that case would let an admin believe a product is live when the storefront
// never received it, so report the miss explicitly.
const STOREFRONT_MISS_REASONS = {
  "no-netsuite-item":
    "it has no matching NetSuite item. Saved in the admin, but the store can't show it until NetSuite has the SKU.",
  "no-sku":
    "it has no SKU. Saved in the admin, but the store can't show it without one.",
  "missing-price":
    "it has no price. Saved in the admin, but the store won't show a product with no price.",
  "no-product": "the server didn't return a product record.",
};

// The IBO cart refuses a bundle whose items aren't all live products, so a
// saved bundle can still be unsellable. Say which item is the problem.
export function bundleStorefrontWarning(bundle) {
  if (!bundle) return null;
  if (bundle.reason === "error") {
    return `Saved, but the bundle's items didn't reach the storefront${bundle.error ? `: ${bundle.error}` : "."}`;
  }
  if (bundle.unresolved && bundle.unresolved.length) {
    return `Saved, but members can't buy this bundle — not in the store: ${bundle.unresolved.join(", ")}.`;
  }
  if (bundle.inactiveComponents && bundle.inactiveComponents.length) {
    return `Saved, but members can't buy this bundle until these items are published: ${bundle.inactiveComponents.join(", ")}.`;
  }
  return null;
}

export function warnIfStorefrontMissed(updated) {
  const result = updated && updated.storefront;
  if (result && result.published) {
    const bundleWarning = bundleStorefrontWarning(result.bundle);
    if (bundleWarning) toast.error(bundleWarning);
    return;
  }
  if (!result) return;
  // No SQL connection configured is a local-dev condition, not a real miss.
  if (result.reason === "sql-not-configured") return;
  const detail =
    STOREFRONT_MISS_REASONS[result.reason] ||
    (result.reason === "error"
      ? `the storefront update failed${
          result.error ? `: ${result.error}` : "."
        }`
      : `the storefront update didn't complete (${result.reason}).`);
  toast.error(`Saved, but this product is not on the storefront — ${detail}`);
}

// Membership and pin level are ICON_SELECTOR_REQUIRED in SQL
// (Store.VisibilityGroupTypes.ComponentTypeId -> Store.ComponentTypes). Kevin's
// publishing rules make "no icons selected" the one combination that is not
// allowed: every other state maps onto a visibility rule, but an empty row is
// ambiguous between "everyone" and "nobody" and cannot be stored either way.
// Selecting all of them is how you say "everyone" — that writes no restriction.
export const AUDIENCE_REQUIRED_ROWS = [
  { field: "memberships", label: "membership" },
  { field: "pins", label: "pin level" },
];

export function audienceSelectionError(snapshot) {
  const missing = AUDIENCE_REQUIRED_ROWS.filter(
    ({ field }) => !(snapshot && snapshot[field] && snapshot[field].length)
  );
  if (!missing.length) return null;
  const rows = missing.map((row) => row.label).join(" and ");
  return (
    `Pick at least one ${rows} in Content Settings. ` +
    "Leaving it empty doesn't mean everyone — select them all for that."
  );
}

// A version is how an editor tells one draft of a product from another, so a
// blank name (which used to save as an empty label) or a name that already
// belongs to a sibling version makes the version picker useless. Both are
// rejected at save time rather than silently accepted.
export function versionNameError(name, versions = [], currentVersionId = null) {
  const trimmed = String(name == null ? "" : name).trim();
  if (!trimmed) {
    return "Give this version a name before saving.";
  }
  const clash = versions.some(
    (v) =>
      v.id !== currentVersionId &&
      String(v.name == null ? "" : v.name)
        .trim()
        .toLowerCase() === trimmed.toLowerCase()
  );
  if (clash) {
    return `Another version is already called "${trimmed}". Version names have to be unique.`;
  }
  return null;
}

function loadStoredVersions(productId) {
  if (!productId) return null;
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(versionsStorageKey(productId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.versions) || !parsed.versions.length) {
      return null;
    }
    return parsed; // { versions: [{ id, name, active, description, content }], currentVersionId }
  } catch (err) {
    return null;
  }
}

function saveStoredVersions(productId, payload) {
  if (!productId) return;
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(
      versionsStorageKey(productId),
      JSON.stringify(payload)
    );
  } catch (err) {
    // Quota exceeded (e.g. large data-URL images) or blocked storage — keep the
    // in-memory versions; persistence is a best-effort convenience here.
  }
}

export function StoreProductEditor({ mode }) {
  const history = useHistory();
  const { productId } = useParams();
  const isEdit = mode === "edit";

  // ----- Server-backed product state (Item 3) -----
  const [serverProduct, setServerProduct] = React.useState(null);
  const [loading, setLoading] = React.useState(isEdit);
  const [loadError, setLoadError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [pendingUndo, setPendingUndo] = React.useState(false);
  const [pendingBack, setPendingBack] = React.useState(false);
  const [pendingSave, setPendingSave] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);

  // Dirty state is DERIVED, not flagged. We snapshot the editable fields once
  // the form is hydrated (and again after each save) into `baseline`, then
  // compare the live snapshot against it. Reverting a change by hand therefore
  // returns the form to a clean state (Save disables, Undo hides) — something a
  // manual boolean flag could never detect. `baseline` also doubles as the
  // restore source for the "Undo Changes" button.
  const [baseline, setBaseline] = React.useState(null);
  // Whether the server considers this product live. `null` until the product
  // loads, so a brand-new product (which never loads one) stays "Push Live".
  const [serverPublished, setServerPublished] = React.useState(null);

  // Bypass the history.block guard below when our own confirmed navigation
  // (doBackToStore, delete, etc.) fires so the custom modal doesn't stack.
  const allowLeaveRef = React.useRef(false);

  const [title, setTitle] = React.useState(
    isEdit ? `Product ${productId}` : ""
  );
  const [description, setDescription] = React.useState("");
  const [author, setAuthor] = React.useState("");
  const [authorSuggestions, setAuthorSuggestions] = React.useState([]);
  const [showAuthorDropdown, setShowAuthorDropdown] = React.useState(false);
  const [sku, setSku] = React.useState("");
  const [netsuiteTitle, setNetsuiteTitle] = React.useState("");
  // Derived server-side from the Store App Categories selection (category =
  // top-level title, productType = deepest selected title). Kept in state only
  // so version snapshots round-trip; the editor no longer edits them directly,
  // and they must NOT default to a placeholder string -- "Default" used to be
  // the initial value here and got persisted as a real product type.
  const [category, setCategory] = React.useState("");
  const [productType, setProductType] = React.useState("");
  // WWG Store App Categories (Content Settings). Multi-select — a product can
  // belong to multiple store-facing categories, mirroring the Category Builder
  // app (7/9 decision, supersedes the old General Information Category/Product
  // dropdowns). Values are the category names.
  // Sourced live from GET /v1/categories via ContentSettingsCard (the Category
  // Builder's `Categories` collection). Values are the category names.
  const [appCategories, setAppCategories] = React.useState([]);
  // Pricing is held as an object of integer-cents fields so it matches the
  // PricingCard contract ({ lastPurchaseCents, premierMemberCents, retailCents })
  // and round-trips through buildPayload/hydration.
  const [pricing, setPricing] = React.useState({
    lastPurchaseCents: null,
    premierMemberCents: null,
    retailCents: null,
    bundledCents: null,
  });
  // NetSuite's own price for this SKU, read live from SQL by the API. Never
  // written back — `pricing` above is the admin override layered over this.
  const [netsuitePricing, setNetsuitePricing] = React.useState(null);
  // The warehouse/NetSuite stock figures for this SKU, read live from SQL by the
  // API. Never written back — `stock` below is the admin override layered on it.
  const [netsuiteStock, setNetsuiteStock] = React.useState(null);
  // A bundle's sellable count, worked out server-side from its components.
  const [bundleStock, setBundleStock] = React.useState(null);
  // Stock figures captured from the Stock card. Strings while editing; persisted
  // verbatim through the mock save so they survive a reload.
  const [stock, setStock] = React.useState({
    currentStock: "",
    vendorBin: "",
    caseQty: "",
    onHand: "",
  });
  const [publishStatus, setPublishStatus] = React.useState("published");
  const [availability, setAvailability] = React.useState("available");
  const [preOrderStart, setPreOrderStart] = React.useState("");
  const [preOrderEnd, setPreOrderEnd] = React.useState("");
  const [publishOnDate, setPublishOnDate] = React.useState("");
  const [publishOnTime, setPublishOnTime] = React.useState("");
  // Tracks whether the publish-on effect auto-promoted "published" -> "scheduled"
  // this session, so clearing the date can undo that specific auto-change without
  // ever touching a status that was hydrated (e.g. a baseline-scheduled product).
  const autoScheduledRef = React.useRef(false);
  const [unpublishOnDate, setUnpublishOnDate] = React.useState(false);
  const [unpublishOnDateStr, setUnpublishOnDateStr] = React.useState("");
  const [unpublishOnTimeStr, setUnpublishOnTimeStr] = React.useState("");
  // Thumbnail is a dedicated slot separate from the product images gallery.
  // Held as { id, url } | null matching the ThumbnailCard contract so the same
  // value can be passed straight through to the component.
  const [thumbnailUrl, setThumbnailUrl] = React.useState(null);
  // The NetSuite asset from Store.Assets. READ-ONLY and deliberately outside the
  // version snapshot: it is the base the admin's upload overrides, not a value
  // the editor owns. Kept separate so it can never be saved back as an override.
  const [netsuiteThumbnailUrl, setNetsuiteThumbnailUrl] = React.useState(null);
  const [productImages, setProductImages] = React.useState([]);
  // Variations are owned by NetSuite (rev 1: no admin-owned variations), so the
  // editor neither reads nor writes them here.

  // ----- Versioning (item-12) -----
  // A version is a NEW COPY of the product (its own name/tags/thumbnail/content)
  // that inherits the original's metadata. Exactly ONE version is active/live at
  // a time (the green dot) — that's the one mirrored to the store. Every version
  // carries its OWN `content` (a full field snapshot); the editor form is just a
  // working copy of the currently-viewed version. Switching versions swaps the
  // form in/out of each version's content; saving commits the form back into the
  // current version, marks it live, and pushes it to the BFF.
  const [versions, setVersions] = React.useState(() => {
    const stored = loadStoredVersions(productId);
    return stored
      ? stored.versions
      : [
          {
            id: "primary",
            name: "Primary",
            active: true,
            description: "",
            content: null, // seeded from the server on first load
          },
        ];
  });
  const [currentVersionId, setCurrentVersionId] = React.useState(() => {
    const stored = loadStoredVersions(productId);
    return stored
      ? stored.currentVersionId || stored.versions[0].id
      : "primary";
  });
  // Pending target version id when a version switch is intercepted by the
  // unsaved-changes prompt (null when no prompt is open).
  const [pendingVersionSwitch, setPendingVersionSwitch] = React.useState(null);
  // Whether the DB-backed version list has been loaded (or resolved as absent).
  // For new products there is nothing to load, so start "loaded". Gates version
  // hydration/persistence so we never overwrite server versions with the local
  // default before the fetch resolves.
  const [versionsLoaded, setVersionsLoaded] = React.useState(
    !(mode === "edit") || !productId
  );
  // Whether it is SAFE to auto-persist the version list to the server. Only true
  // once the server load has definitively succeeded (returned versions OR a
  // confirmed-empty result) or for a brand-new product. If the load FAILS
  // (network/5xx/transient), this stays false so the auto-persist effect never
  // overwrites the server's real version list with the local default — that
  // would silently destroy every server-side version. New products start ready.
  const versionsPersistOkRef = React.useRef(!(mode === "edit") || !productId);
  // The version list as the server last had it (stable JSON), so opening a
  // product doesn't write its unchanged versions straight back.
  const persistedVersionsRef = React.useRef(null);
  // Live mirror of currentVersionId so async continuations (image uploads)
  // can detect a version switch that happened while they were in flight.
  const currentVersionIdRef = React.useRef(currentVersionId);
  currentVersionIdRef.current = currentVersionId;
  const currentVersion =
    versions.find((v) => v.id === currentVersionId) || versions[0];

  // Persist the whole version list (content included) + the viewed version to
  // localStorage. Accepts explicit next values so callers can persist state they
  // are about to set without waiting for the re-render.
  //
  // The ACTIVE version's content is intentionally dropped before persisting: the
  // BFF is its source of truth (see the init effect), so storing it locally is
  // both redundant and the main cause of quota failures (large data-URL images).
  // Keeping only draft content well under the ~5MB budget means draft versions
  // reliably survive a reload.
  const saveVersions = React.useCallback(
    (nextVersions = versions, nextCurrentId = currentVersionId) => {
      const trimmed = nextVersions.map((v) =>
        v.active ? { ...v, content: null } : v
      );
      const payload = {
        versions: trimmed,
        currentVersionId: nextCurrentId,
      };
      // localStorage is a fast, offline-friendly cache; the DB is the shared
      // source of truth so versions created on one machine appear on another.
      saveStoredVersions(productId, payload);
      if (!productId) return;
      const json = stableJson(payload);
      if (json === persistedVersionsRef.current) return;
      persistedVersionsRef.current = json;
      versionsApi.save(productId, payload).catch(() => {
        persistedVersionsRef.current = null;
      });
    },
    [productId, versions, currentVersionId]
  );

  // ----- Product flag toggles (Item 10) -----
  const [flags, setFlags] = React.useState({
    isNew: false,
    markAsNewUntil: null,
    isReadable: false,
    isBundle: false,
    source: "wwg",
    thirdPartyName: "",
  });

  const [bundlePricingMode, setBundlePricingMode] = React.useState("evenly");
  const [bundledProducts, setBundledProducts] = React.useState([]);

  const [memberships, setMemberships] = React.useState(DEFAULT_MEMBERSHIPS);
  const [pins, setPins] = React.useState(DEFAULT_PINS);
  const [tagNames, setTagNames] = React.useState([]);
  // Family-reunion and diamond group selections share one id list. Each section
  // in ContentSettingsCard only renders (and can toggle) its own category's
  // groups, so a single source of truth stays correct without cross-category
  // pollution — and unchecking a group actually persists.
  const [audienceGroupIds, setAudienceGroupIds] = React.useState([]);
  const [additionalIbos, setAdditionalIbos] = React.useState([]);
  // Version Details (name/description) live in a self-contained card with an
  // explicit Save button and are NOT part of buildSnapshot()/isDirty, so we
  // track their unsaved state here to guard against silent loss on navigate or
  // refresh.
  const [versionDetailsDirty, setVersionDetailsDirty] = React.useState(false);
  // Latest version name/description reported by the VersionDetailsCard, read at
  // save time so the main Save persists them together with the rest of the form.
  const versionDetailsRef = React.useRef({});
  const handleVersionDetailsChange = React.useCallback((name, desc) => {
    versionDetailsRef.current = { name, desc };
  }, []);

  // Serialized snapshot of every user-editable field. This is the single source
  // of truth for "has the form changed" — compared against `baseline` to derive
  // `isDirty`, and used object-wise to power Undo.
  const buildSnapshot = React.useCallback(
    (overrides) => ({
      title,
      description,
      author,
      category,
      productType,
      pricing,
      stock,
      publishStatus,
      availability,
      publishOnDate,
      publishOnTime,
      unpublishOnDate,
      unpublishOnDateStr,
      unpublishOnTimeStr,
      preOrderStart,
      preOrderEnd,
      thumbnailUrl,
      productImages,
      flags,
      memberships,
      pins,
      tagNames,
      audienceGroupIds,
      additionalIbos,
      appCategories,
      bundlePricingMode,
      bundledProducts,
      ...overrides,
    }),
    [
      title,
      description,
      author,
      category,
      productType,
      pricing,
      stock,
      publishStatus,
      availability,
      publishOnDate,
      publishOnTime,
      unpublishOnDate,
      unpublishOnDateStr,
      unpublishOnTimeStr,
      preOrderStart,
      preOrderEnd,
      thumbnailUrl,
      productImages,
      flags,
      memberships,
      pins,
      tagNames,
      audienceGroupIds,
      additionalIbos,
      appCategories,
      bundlePricingMode,
      bundledProducts,
    ]
  );

  const isDirty = React.useMemo(() => {
    if (baseline === null) return false;
    // Compare against a NORMALIZED baseline. `restoreSnapshot` coerces a handful
    // of optional fields to defaults (e.g. `preOrderStart || ""`,
    // `bundlePricingMode || "evenly"`), so the live form snapshot always carries
    // those defaults. A stored version's raw content may instead hold null /
    // undefined for the same fields, which would otherwise read as a phantom
    // "unsaved change" the moment you switch to an untouched version. Normalizing
    // both sides makes the comparison apples-to-apples.
    const snap = buildSnapshot();
    const a = normalizeSnapshot({ ...snap, productImages: undefined });
    const b = normalizeSnapshot({ ...baseline, productImages: undefined });
    // Compare productImages separately via a stable projection (ordered list of
    // image identities). Preview images upload to blob immediately but only get
    // persisted onto the product on Save, so add/remove/reorder must mark the
    // form dirty — otherwise Save stays disabled and the
    // unsaved-changes guard never fires for image-only edits. Projecting to
    // url/id avoids phantom diffs from incidental asset metadata.
    const imagesKey = (imgs) =>
      (Array.isArray(imgs) ? imgs : [])
        .map((img) =>
          typeof img === "string" ? img : (img && (img.url || img.id)) || ""
        )
        .join("|");
    return (
      JSON.stringify(sortObjectKeys(a)) !== JSON.stringify(sortObjectKeys(b)) ||
      imagesKey(snap.productImages) !== imagesKey(baseline.productImages)
    );
  }, [baseline, buildSnapshot]);

  // BugHerd #60: the header's primary button reflects whether the product is
  // already live. Never published -> green "Push Live"; already live -> navy
  // "Push Revisions". Anchored to the status the SERVER returned on load (and
  // re-anchored after each successful save) rather than the live
  // `publishStatus` state, so picking "Published" in the form doesn't relabel
  // the button before the change has actually been saved. Deliberately not
  // derived from `baseline`: that is only set once the version list resolves,
  // which on a slow DEV response left an already-live product showing a green
  // "Push Live" for several seconds.
  const isLive = isEdit && serverPublished === true;
  // On an edit screen neither state is known until the server responds, so the
  // button is held back for that window instead of being rendered in a state
  // that may be wrong. It is disabled while loading anyway, so nothing is lost.
  const liveStateKnown = !isEdit || serverPublished !== null;

  React.useEffect(() => {
    if (!isDirty && !versionDetailsDirty) return undefined;
    const unblock = history.block(() => {
      if (allowLeaveRef.current) return undefined;
      setPendingBack(true);
      return false;
    });
    return unblock;
  }, [isDirty, versionDetailsDirty, history]);

  // Warn on browser refresh/close while Version Details have unsaved edits.
  // The main form autosaves (and restores via toast), but version name/desc do
  // not, so this is the only guard against losing them on a hard reload.
  React.useEffect(() => {
    if (!versionDetailsDirty) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [versionDetailsDirty]);

  // Push a snapshot object back into the live form state. Shared by Undo
  // (restore baseline), version switching, and restoring a stored non-primary
  // version after a page reload.
  const restoreSnapshot = React.useCallback((snap) => {
    if (!snap) return;
    setTitle(snap.title);
    setDescription(snap.description);
    setAuthor(snap.author);
    setCategory(snap.category);
    setProductType(snap.productType);
    setAppCategories(snap.appCategories);
    setPricing(snap.pricing);
    setStock(snap.stock);
    setPublishStatus(snap.publishStatus);
    setAvailability(snap.availability);
    setPreOrderStart(snap.preOrderStart || "");
    setPreOrderEnd(snap.preOrderEnd || "");
    setPublishOnDate(snap.publishOnDate || "");
    setPublishOnTime(snap.publishOnTime || "");
    setUnpublishOnDate(snap.unpublishOnDate);
    setUnpublishOnDateStr(snap.unpublishOnDateStr || "");
    setUnpublishOnTimeStr(snap.unpublishOnTimeStr || "");
    setThumbnailUrl(snap.thumbnailUrl);
    setProductImages(snap.productImages);
    setFlags(snap.flags);
    setMemberships(snap.memberships);
    setPins(snap.pins);
    setTagNames(snap.tagNames);
    setAudienceGroupIds(snap.audienceGroupIds || []);
    setAdditionalIbos(snap.additionalIbos);
    setBundlePricingMode(snap.bundlePricingMode || "evenly");
    setBundledProducts(snap.bundledProducts || []);
  }, []);

  // Commit the current form into the current version's content, returning the
  // updated version list. Used whenever we leave a version (switch/save) so its
  // in-progress edits are kept with it.
  const commitCurrent = React.useCallback(
    (list) =>
      list.map((v) =>
        v.id === currentVersionId ? { ...v, content: buildSnapshot() } : v
      ),
    [currentVersionId, buildSnapshot]
  );

  // One-time initialization once the form is ready (immediately for new
  // products, or after the GET hydrates state for existing ones).
  //
  // SOURCE-OF-TRUTH RULE: the BFF product record is authoritative for the
  // LIVE/active version — Save (on the live version) and "Set Live" push the
  // whole snapshot there, so on every reload we refresh the active version's
  // content from the freshly-hydrated server values. This is what makes saved
  // thumbnails and product images stick: they round-trip through
  // the server, not fragile localStorage (which also silently drops large
  // data-URL images once the quota is hit).
  //
  // localStorage only carries NON-ACTIVE draft versions (there's no versions
  // endpoint yet), so their in-progress content is restored as-is.
  // `baseline` mirrors the current version's content and drives isDirty.
  const ready = !isEdit || (!loading && serverProduct !== null);

  // Load the DB-backed version list (source of truth, shared across devices).
  // Re-runs only when the product changes. If the server has versions, they
  // replace the local default/cache; otherwise we keep the local state and let
  // the persist effect seed the server. Always caches to localStorage for
  // fast/offline reloads. Uses only a `cancelled` guard (no persistent ref) so
  // React 18 StrictMode's mount→unmount→remount cycle still applies the fetch.
  React.useEffect(() => {
    if (!isEdit || !productId) {
      setVersionsLoaded(true);
      return undefined;
    }
    let cancelled = false;
    setVersionsLoaded(false);
    versionsPersistOkRef.current = false;
    versionsApi
      .get(productId)
      .then((doc) => {
        if (cancelled) return;
        if (doc && Array.isArray(doc.versions) && doc.versions.length) {
          const live = doc.versions.find((v) => v.active) || doc.versions[0];
          const nextId = doc.currentVersionId || live.id;
          setVersions(doc.versions);
          setCurrentVersionId(nextId);
          persistedVersionsRef.current = stableJson({
            versions: doc.versions.map((v) =>
              v.active ? { ...v, content: null } : v
            ),
            currentVersionId: nextId,
          });
          saveStoredVersions(productId, {
            versions: doc.versions,
            currentVersionId: nextId,
          });
        }
        // Load succeeded (has versions, or is legitimately empty) → auto-persist
        // is now safe.
        versionsPersistOkRef.current = true;
        setVersionsLoaded(true);
      })
      .catch(() => {
        // Load FAILED. Let the editor function, but keep auto-persist disabled so
        // we never clobber the server's real version list with the local default.
        if (!cancelled) setVersionsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, productId]);

  const didInitVersionsRef = React.useRef(false);
  React.useEffect(() => {
    if (!ready || !versionsLoaded || didInitVersionsRef.current) return;
    didInitVersionsRef.current = true;
    const serverSnapshot = buildSnapshot();

    // Refresh the active version (and any version that has never been seeded)
    // from the server; leave stored draft content untouched.
    setVersions((prev) =>
      prev.map((v) =>
        v.active || !v.content ? { ...v, content: serverSnapshot } : v
      )
    );

    const cur = versions.find((v) => v.id === currentVersionId) || versions[0];
    if (cur && cur.content && !cur.active) {
      // Viewing a stored draft: restore its own in-progress content.
      restoreSnapshot(cur.content);
      setBaseline(cur.content);
    } else {
      // Viewing the live version (or a brand-new primary): the form already
      // holds the hydrated server values, so just anchor the baseline to them.
      setBaseline(serverSnapshot);
    }
  }, [
    ready,
    versionsLoaded,
    buildSnapshot,
    restoreSnapshot,
    versions,
    currentVersionId,
  ]);

  // Restore server-side autosave after editor is fully initialized.
  // Delete stale autosaves that match the server product.
  React.useEffect(() => {
    if (!ready || !isEdit || !productId) return undefined;
    // Guard against a slow response for one version restoring its draft into
    // another version after a quick version switch (the request is keyed on
    // currentVersionId, so a stale resolve must be ignored).
    let cancelled = false;
    const snap = buildSnapshot();
    autosavesApi
      .get(productId, currentVersionId)
      .then((autosave) => {
        if (cancelled) return;
        if (!autosave || !autosave.content) return;
        const autosaveTime = new Date(autosave.updatedAt).getTime();
        const saveTime = serverProduct?.updatedAt
          ? new Date(serverProduct.updatedAt).getTime()
          : 0;
        // A draft is only safe to restore onto the copy it was made from. A
        // draft written by another tab still showing an older copy is newer
        // than the last save by the clock, but restoring it silently undoes
        // that save, and the next Save would push it with no conflict.
        const { baseVersion, ...draft } = autosave.content;
        const stale =
          baseVersion != null &&
          serverProduct?.version != null &&
          baseVersion !== serverProduct.version;
        if (autosaveTime <= saveTime || stale) {
          autosavesApi.remove(productId, currentVersionId).catch(() => {});
          if (stale) {
            toast("Unsaved changes from before the last save were discarded", {
              icon: "⚠️",
            });
          }
          return;
        }
        autosave.content = draft;
        if (
          autosave.content.thumbnailUrl &&
          typeof autosave.content.thumbnailUrl === "string"
        ) {
          autosave.content.thumbnailUrl = {
            url: autosave.content.thumbnailUrl,
          };
        }
        restoreSnapshot({ ...snap, ...autosave.content });
        toast("Unsaved changes restored", { icon: "💾" });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [ready, currentVersionId]);

  // Persist the whole version list (content included) + the viewed version to
  // localStorage AND the DB (via saveVersions) whenever they change, so versions
  // survive a reload and are shared across devices. Gated on `versionsLoaded` so
  // we don't clobber server versions with the local default before the fetch
  // resolves, and on `versionsPersistOkRef` so a FAILED load never overwrites the
  // server's real version list with the local default (silent data loss).
  React.useEffect(() => {
    if (!ready || !versionsLoaded || !versionsPersistOkRef.current) return;
    saveVersions();
  }, [ready, versionsLoaded, versions, currentVersionId, saveVersions]);

  function sortObjectKeys(v) {
    if (Array.isArray(v)) return v.map(sortObjectKeys);
    if (v && typeof v === "object") {
      return Object.keys(v)
        .sort()
        .reduce((o, k) => {
          o[k] = sortObjectKeys(v[k]);
          return o;
        }, {});
    }
    return v;
  }

  // Apply the exact same field coercions that `restoreSnapshot` performs when it
  // loads a snapshot into the form. Used to normalize both sides of the isDirty
  // comparison so a version's stored null/undefined optionals don't masquerade
  // as unsaved edits once they're coerced to "" / defaults in the live form.
  function normalizeSnapshot(snap) {
    if (!snap) return snap;
    return {
      ...snap,
      preOrderStart: snap.preOrderStart || "",
      preOrderEnd: snap.preOrderEnd || "",
      publishOnDate: snap.publishOnDate || "",
      publishOnTime: snap.publishOnTime || "",
      unpublishOnDateStr: snap.unpublishOnDateStr || "",
      unpublishOnTimeStr: snap.unpublishOnTimeStr || "",
      bundlePricingMode: snap.bundlePricingMode || "evenly",
      bundledProducts: snap.bundledProducts || [],
    };
  }

  // Auto-save to server whenever the form is dirty, so unsaved changes
  // survive page refreshes and browser crashes. Delete autosave when all changes
  // are reverted.
  const autosaveTimerRef = React.useRef(null);
  const autosaveSavedRef = React.useRef(false);
  React.useEffect(() => {
    if (!isEdit || !productId) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (!isDirty) {
      if (autosaveSavedRef.current) {
        autosaveSavedRef.current = false;
        autosavesApi.remove(productId, currentVersionId).catch(() => {});
      }
      return;
    }
    autosaveTimerRef.current = setTimeout(() => {
      const snap = buildSnapshot();
      const autosaveContent = {
        ...snap,
        baseVersion: currentVersion?.active
          ? serverProduct?.version ?? null
          : null,
        thumbnailUrl: snap.thumbnailUrl
          ? { url: snap.thumbnailUrl.url, id: snap.thumbnailUrl.id }
          : null,
        // Gallery images are uploaded to blob storage on select (mediaApi.upload
        // returns a persisted {id,url,...} asset), so they're JSON-serializable
        // and safe to autosave. Persisting them means images added but not yet
        // published survive a refresh/crash restore instead of being silently
        // dropped (previously this was forced to undefined).
        productImages: Array.isArray(snap.productImages)
          ? snap.productImages
          : [],
      };
      autosavesApi
        .save(productId, currentVersionId, autosaveContent)
        .then(() => {
          autosaveSavedRef.current = true;
        })
        .catch(() => {});
    }, 2000);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [
    isDirty,
    isEdit,
    productId,
    currentVersionId,
    buildSnapshot,
    currentVersion?.active,
    serverProduct?.version,
  ]);

  // Hydrate form state from server (Item 3). New-product mode skips the GET
  // and falls back to empty defaults.
  React.useEffect(() => {
    if (!isEdit || !productId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    api
      .get(productId)
      .then((product) => {
        if (cancelled || !product) return;
        setServerProduct(product);
        if (product.name) setTitle(product.name);
        if (typeof product.description === "string") {
          setDescription(product.description);
        }
        if (product.author) setAuthor(product.author);
        if (product.sku) setSku(product.sku);
        if (product.netsuiteTitle) setNetsuiteTitle(product.netsuiteTitle);
        // Only an admin upload belongs in the editable slot. The NetSuite asset
        // is passed to ThumbnailCard separately as a base image, so opening and
        // saving a product can't turn it into a fake override.
        if (product.thumbnailUrl || product.image) {
          setThumbnailUrl({
            id: product.thumbnailId || null,
            url:
              product.thumbnailUrl?.url ||
              product.thumbnailUrl ||
              product.image,
          });
        }
        setNetsuiteThumbnailUrl(
          product.thumbnailSource === "netsuite"
            ? product.effectiveThumbnailUrl || null
            : product.netsuiteImages?.thumbnailIsRelative
            ? null
            : product.netsuiteImages?.thumbnailUrl || null
        );
        if (product.pricing && typeof product.pricing === "object") {
          setPricing({
            lastPurchaseCents: product.pricing.lastPurchaseCents ?? null,
            premierMemberCents: product.pricing.premierMemberCents ?? null,
            retailCents: product.pricing.retailCents ?? null,
            bundledCents: product.pricing.bundledCents ?? null,
          });
        }
        // NetSuite's price, read live from SQL. Read-only reference: the editor
        // only ever writes `pricing`, which is the override layer on top of it.
        setNetsuitePricing(product.netsuitePricing || null);
        setNetsuiteStock(product.netsuiteStock || null);
        setBundleStock(product.bundleStock || null);
        if (product.bundlePricingMode) {
          setBundlePricingMode(product.bundlePricingMode);
        }
        if (Array.isArray(product.bundledProducts)) {
          setBundledProducts(product.bundledProducts);
        }
        if (product.stock && typeof product.stock === "object") {
          setStock({
            currentStock: product.stock.currentStock ?? "",
            vendorBin: product.stock.vendorBin ?? "",
            caseQty: product.stock.caseQty ?? "",
            onHand: product.stock.onHand ?? "",
          });
        }
        if (
          Array.isArray(product.productImages) &&
          product.productImages.length > 0
        ) {
          setProductImages(product.productImages);
        }
        if (product.category) setCategory(product.category);
        // `type` is the WWG/3rd source classifier (surfaced via flags.source);
        // the editor's Product Type dropdown maps to `productType`.
        if (product.productType) setProductType(product.productType);
        if (Array.isArray(product.storeAppCategories))
          setAppCategories(product.storeAppCategories);
        // Top-level `status` is the canonical field the store list, filters and
        // archive actions all key off, so prefer it here and only fall back to
        // the nested publish.status mirror. Keeps the editor pill in sync with
        // the catalog card.
        let loadedStatus = null;
        if (product.status) {
          loadedStatus = normalizePublishStatus(product.status);
        } else if (product.publish && product.publish.status) {
          // Normalize backend casing — endpoint doc uses "In Review" etc.,
          // editor pill uses lowercase snake/short keys.
          loadedStatus = normalizePublishStatus(
            String(product.publish.status).toLowerCase().replace(/\s+/g, "_")
          );
        }
        if (loadedStatus) {
          setPublishStatus(loadedStatus);
          setServerPublished(loadedStatus === "published");
        }
        if (product.availability) {
          // Normalize legacy/seed casing ("Out of Stock", "Pre-Order") to the
          // canonical backend enum keys the buttons use.
          setAvailability(
            String(product.availability).toLowerCase().replace(/[\s-]/g, "")
          );
        }
        if (product.preOrderStart)
          setPreOrderStart(String(product.preOrderStart).slice(0, 10));
        if (product.preOrderEnd)
          setPreOrderEnd(String(product.preOrderEnd).slice(0, 10));
        if (product.publish && product.publish.unpublishAt) {
          const uAt = new Date(product.publish.unpublishAt);
          if (!Number.isNaN(uAt.getTime())) {
            setUnpublishOnDate(true);
            setUnpublishOnDateStr(toDateInputValue(uAt));
            setUnpublishOnTimeStr(toTimeInputValue(uAt));
          }
        }
        if (product.publish && product.publish.publishAt) {
          const publishAt = new Date(product.publish.publishAt);
          if (!Number.isNaN(publishAt.getTime())) {
            setPublishOnDate(toDateInputValue(publishAt));
            setPublishOnTime(toTimeInputValue(publishAt));
          } else {
            setPublishOnDate("");
            setPublishOnTime("");
          }
        } else {
          setPublishOnDate("");
          setPublishOnTime("");
        }
        if (Array.isArray(product.tags)) {
          setTagNames(product.tags);
        }
        setPins(audienceCodesOrDefault(product.audiencePinCodes, DEFAULT_PINS));
        setMemberships(
          audienceCodesOrDefault(
            product.audienceMembershipCodes,
            DEFAULT_MEMBERSHIPS
          )
        );
        if (Array.isArray(product.audienceGroupIds)) {
          setAudienceGroupIds(product.audienceGroupIds);
        }
        if (Array.isArray(product.additionalIbos)) {
          setAdditionalIbos(product.additionalIbos);
        }
        setFlags({
          isNew: Boolean(product.isNew),
          markAsNewUntil: product.markAsNewUntil || null,
          isReadable: Boolean(product.isReadable),
          isBundle: Boolean(product.isBundle),
          source: product.source || "wwg",
          thirdPartyName: product.thirdPartyName || "",
        });
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isEdit, productId]);

  // Variations are NetSuite-owned (rev 1): the editor does not hydrate them.

  React.useEffect(() => {
    if (!author || author.length < 2) {
      setAuthorSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      attributesApi
        .list(author)
        .then((attrs) => {
          const authorAttr = attrs.find((a) => a.AttributeType === "Author");
          if (authorAttr && authorAttr.Attributes) {
            setAuthorSuggestions(
              authorAttr.Attributes.map((a) => a.Attribute).slice(0, 8)
            );
          } else {
            setAuthorSuggestions([]);
          }
        })
        .catch(() => setAuthorSuggestions([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [author]);

  // Keep publishStatus in sync with publishOnDate/publishOnTime:
  // - setting a future datetime auto-switches "published" -> "scheduled"
  // - when the moment arrives, switches "scheduled" -> "published"
  React.useEffect(() => {
    const publishAt = parsePublishAt(publishOnDate, publishOnTime);
    if (!publishAt) {
      // Date cleared. If WE auto-promoted published -> scheduled earlier this
      // session, undo it so the product doesn't linger on "Scheduled" with no
      // date. Never revert a status we didn't set (e.g. a hydrated schedule).
      if (publishStatus === "scheduled" && autoScheduledRef.current) {
        autoScheduledRef.current = false;
        setPublishStatus("published");
      }
      return undefined;
    }

    if (publishAt.getTime() > Date.now() && publishStatus === "published") {
      autoScheduledRef.current = true;
      setPublishStatus("scheduled");
    }

    // On page load (or re-hydration), if the scheduled time has already passed
    // flip to "published" so the editor doesn't show a stale status.
    if (publishAt.getTime() <= Date.now() && publishStatus === "scheduled") {
      autoScheduledRef.current = false;
      setPublishStatus("published");
      return undefined;
    }

    // Only a *scheduled* product should auto-advance to "published" when its
    // publish time arrives. An in_review / archived product may still carry a
    // stale past publishAt from a prior scheduling; it must never be silently
    // flipped to Published (which could republish it on Save).
    if (publishStatus !== "scheduled") return undefined;

    let timeoutId;
    const scheduleFlip = () => {
      const remaining = publishAt.getTime() - Date.now();
      if (remaining <= 0) {
        setPublishStatus("published");
        return;
      }
      timeoutId = window.setTimeout(
        scheduleFlip,
        Math.min(remaining, 2147483647)
      );
    };
    scheduleFlip();
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [publishOnDate, publishOnTime, publishStatus]);

  // Keep publishStatus in sync with unpublishOnDate/unpublishOnTime:
  // - when the moment arrives, drops back to "In Review"
  React.useEffect(() => {
    if (!unpublishOnDate) return undefined;
    const unpublishAt = parsePublishAt(unpublishOnDateStr, unpublishOnTimeStr);
    if (!unpublishAt) return undefined;

    const allowed = ["published", "scheduled", "in_review"];
    if (!allowed.includes(publishStatus)) return undefined;

    // On page load, if the unpublish time has already passed drop it out of live
    if (unpublishAt.getTime() <= Date.now()) {
      setPublishStatus("in_review");
      return undefined;
    }

    let timeoutId;
    const scheduleUnpublish = () => {
      const remaining = unpublishAt.getTime() - Date.now();
      if (remaining <= 0) {
        setPublishStatus("in_review");
        return;
      }
      timeoutId = window.setTimeout(
        scheduleUnpublish,
        Math.min(remaining, 2147483647)
      );
    };
    scheduleUnpublish();
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [unpublishOnDate, unpublishOnDateStr, unpublishOnTimeStr, publishStatus]);

  // Auto-expire the "New!" flag when markAsNewUntil arrives.
  React.useEffect(() => {
    if (!flags.isNew || !flags.markAsNewUntil) return undefined;
    const until = new Date(flags.markAsNewUntil);
    if (Number.isNaN(until.getTime())) return undefined;

    if (until.getTime() <= Date.now()) {
      setFlags((prev) => ({ ...prev, isNew: false, markAsNewUntil: null }));
      return undefined;
    }

    let timeoutId;
    const scheduleExpire = () => {
      const remaining = until.getTime() - Date.now();
      if (remaining <= 0) {
        setFlags((prev) => ({ ...prev, isNew: false, markAsNewUntil: null }));
        return;
      }
      timeoutId = window.setTimeout(
        scheduleExpire,
        Math.min(remaining, 2147483647)
      );
    };
    scheduleExpire();
    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [flags.isNew, flags.markAsNewUntil]);

  // Map a version content snapshot (see buildSnapshot) to the BFF product
  // payload. Snapshot-based (not live-state-based) so we can publish ANY
  // version's content — e.g. "Set Live" on a version you're not currently
  // editing — not just the form on screen.
  const payloadFromSnapshot = React.useCallback(
    (snap, overrides = {}) => ({
      name: snap.title,
      description: snap.description,
      author: snap.author,
      category: snap.category,
      // `type` is the source classifier the catalog's WWG/3rd filter keys on;
      // the Product Type dropdown persists separately as `productType`.
      type: snap.flags.source === "third_party" ? "3rd" : "WWG",
      productType: snap.productType,
      storeAppCategories: snap.appCategories,
      isNew: Boolean(snap.flags.isNew),
      markAsNewUntil: snap.flags.markAsNewUntil || null,
      isReadable: Boolean(snap.flags.isReadable),
      isBundle: Boolean(snap.flags.isBundle),
      thirdPartyName:
        snap.flags.source === "third_party"
          ? snap.flags.thirdPartyName || ""
          : "",
      source: snap.flags.source || "wwg",
      availability: snap.availability,
      preOrderStart: snap.preOrderStart || null,
      preOrderEnd: snap.preOrderEnd || null,
      pricing: snap.pricing,
      stock: snap.stock,
      thumbnailUrl: snap.thumbnailUrl?.url || null,
      thumbnailId: snap.thumbnailUrl?.id || null,
      // null (not undefined) so removing the thumbnail actually clears the
      // persisted `image` — JSON.stringify drops undefined keys and the
      // backend PATCH only $sets fields present in the payload.
      image: snap.thumbnailUrl?.url || null,
      productImages: snap.productImages,
      tags: snap.tagNames,
      audiencePinCodes: snap.pins,
      audienceMembershipCodes: snap.memberships,
      audienceGroupIds: snap.audienceGroupIds || [],
      additionalIbos: snap.additionalIbos,
      bundlePricingMode: snap.bundlePricingMode || "evenly",
      bundledProducts: snap.bundledProducts || [],
      // Keep the canonical top-level status in lockstep with the
      // publish pill so the catalog card and editor never diverge.
      status: overrides.publishStatus || snap.publishStatus,
      publish: {
        ...(serverProduct && serverProduct.publish
          ? serverProduct.publish
          : {}),
        status: overrides.publishStatus || snap.publishStatus,
        publishAt: (() => {
          const d = parsePublishAt(snap.publishOnDate, snap.publishOnTime);
          return d ? d.toISOString() : null;
        })(),
        unpublishAt: (() => {
          const d = snap.unpublishOnDate
            ? parsePublishAt(snap.unpublishOnDateStr, snap.unpublishOnTimeStr)
            : null;
          return d ? d.toISOString() : null;
        })(),
      },
      ...overrides.extra,
    }),
    [serverProduct]
  );

  // Push a version's content to the BFF product record (what the store reads).
  // This is the single "publish to store" seam shared by Save ("Push
  // Revisions") and "Set Live". Variations are NetSuite-owned and not written
  // from here.
  const publishSnapshotToBff = React.useCallback(
    async (snap, statusOverride) => {
      const payload = {
        ...payloadFromSnapshot(
          snap,
          statusOverride ? { publishStatus: statusOverride } : {}
        ),
        _version: serverProduct && serverProduct.version,
      };
      const updated = await api.update(productId, payload);
      const next = updated || payload;
      // NetSuite-owned fields (variations, pricing, stock) are read-only and are
      // never part of the write payload, so never let a save response drop them
      // from local state — that blanks the Variations panel until a reload.
      setServerProduct((prev) => (prev ? { ...prev, ...next } : next));
      return next;
    },
    [payloadFromSnapshot, productId]
  );

  const persist = React.useCallback(
    async ({ successMessage, statusOverride } = {}) => {
      if (!productId) {
        toast.error("Cannot save: missing product id.");
        return;
      }
      setSaving(true);
      // The saved snapshot carries the current Publish Status pill, so the
      // version content AND the baseline reflect it and the editor doesn't go
      // spuriously "Unsaved" immediately after a successful save.
      const snapshot = buildSnapshot(
        statusOverride ? { publishStatus: statusOverride } : undefined
      );
      const audienceError = audienceSelectionError(snapshot);
      if (audienceError) {
        setSaving(false);
        toast.error(audienceError);
        return;
      }
      const pendingDetails = versionDetailsRef.current || {};
      const pendingName =
        pendingDetails.name !== undefined
          ? pendingDetails.name
          : (versions.find((v) => v.id === currentVersionId) || {}).name;
      const nameError = versionNameError(
        pendingName,
        versions,
        currentVersionId
      );
      if (nameError) {
        setSaving(false);
        toast.error(nameError);
        return;
      }
      // Saving to a status is a real edit to the pill, so move the control too —
      // otherwise the header would disagree with what was just written.
      if (statusOverride) setPublishStatus(statusOverride);
      try {
        // Saving persists the version you're editing. It NEVER changes which
        // version is live — that is exclusively the job of "Set Live". Only the
        // live version mirrors to the BFF product record (the store reads that
        // record); saving a draft version keeps its content without touching the
        // live listing.
        const isLiveVersion = versions.some(
          (v) => v.id === currentVersionId && v.active
        );
        if (isLiveVersion) {
          const updated = await publishSnapshotToBff(snapshot);
          warnIfStorefrontMissed(updated);
          // Re-anchor the header button: saving the live version as "published"
          // is what actually makes it live, so the next render must read
          // "Push Revisions".
          setServerPublished(
            (statusOverride || snapshot.publishStatus) === "published"
          );
        }
        // Commit the form into the current version's content and persist,
        // leaving the active/live flags untouched. `baseline` follows the
        // just-saved snapshot so isDirty clears. Version name/description are
        // folded in here (from the VersionDetailsCard) so a single Save covers
        // the whole editor and never leaves the details spuriously "unsaved".
        const vd = versionDetailsRef.current || {};
        const committed = versions.map((v) =>
          v.id === currentVersionId
            ? {
                ...v,
                content: snapshot,
                name: vd.name !== undefined ? String(vd.name).trim() : v.name,
                description: vd.desc !== undefined ? vd.desc : v.description,
              }
            : v
        );
        setVersions(committed);
        setBaseline(snapshot);
        setVersionDetailsDirty(false);
        saveVersions(committed, currentVersionId);
        toast.success(successMessage || "Product saved");
        autosaveSavedRef.current = false;
        autosavesApi.remove(productId, currentVersionId).catch(() => {});
        // Hand the committed list back so callers that continue synchronously
        // (e.g. Save & Switch) don't operate on a stale `versions` closure and
        // clobber the just-saved name/description.
        return committed;
      } catch (err) {
        toast.error(errorMessage(err, "Couldn't save your changes."));
        return null;
      } finally {
        setSaving(false);
      }
    },
    [
      buildSnapshot,
      publishSnapshotToBff,
      productId,
      versions,
      currentVersionId,
      saveVersions,
    ]
  );

  // Save overwrites the saved record and clears the Undo baseline, so it's
  // irreversible. Gate it behind a confirmation modal (see pendingSave).
  const handleSave = () => {
    setPendingSave(true);
  };
  const confirmSave = async () => {
    setPendingSave(false);
    await persist({ successMessage: "Product saved" });
  };

  const doBackToStore = () => {
    // SPA navigation (NOT a full reload) so the catalog keeps its search,
    // filters, sort, pagination and scroll position — the Store container leaves
    // the list mounted and bumps a refreshKey on return, which refetches and
    // surfaces the edit we just made. Set the bypass flag first so the <Prompt>
    // unsaved-changes guard doesn't fire its native dialog on top of our own
    // confirmation modal.
    allowLeaveRef.current = true;
    const storePath = history.location.pathname.replace(
      /\/(?:add|[^/]+\/edit)$/,
      ""
    );
    history.push(storePath || "/store/home");
  };
  const handleBackToStore = () => {
    if (isDirty || versionDetailsDirty) {
      setPendingBack(true);
      return;
    }
    doBackToStore();
  };

  // Revert every editable field to the last-saved snapshot. Pure
  // restore-to-baseline (the meeting's full change-history/restore is deferred).
  // Destructive, so it's gated behind a confirmation modal (see pendingUndo).
  const confirmUndo = () => {
    if (baseline === null) {
      setPendingUndo(false);
      return;
    }
    restoreSnapshot(baseline);
    setPendingUndo(false);
  };

  // ----- Version tab handlers (item-12) -----
  // Switch which version is being viewed/edited: commit the outgoing version's
  // current form into its content, then load the incoming version's content into
  // the form. Each version is self-contained, so this is a straight swap.
  const doSelectVersion = (nextId, baseVersions = versions) => {
    if (nextId === currentVersionId) return;
    // Keep in-progress version name/description with the outgoing version too —
    // the switch prompt tells the user "edits are kept with their version", and
    // that must include the Version Details card, not just the content.
    const vd = versionDetailsRef.current || {};
    const committed = commitCurrent(baseVersions).map((v) =>
      v.id === currentVersionId
        ? {
            ...v,
            name: vd.name !== undefined ? vd.name : v.name,
            description: vd.desc !== undefined ? vd.desc : v.description,
          }
        : v
    );
    const target = committed.find((v) => v.id === nextId);
    const targetContent =
      target && target.content ? target.content : buildSnapshot();
    restoreSnapshot(targetContent);
    setBaseline(targetContent);
    setVersions(committed);
    setCurrentVersionId(nextId);
    saveVersions(committed, nextId);
  };

  // Switch to another version. If the current version has unsaved edits, prompt
  // first so the user can Save (push live), discard, or cancel (see
  // pendingVersionSwitch modal). Otherwise switch immediately.
  const handleSelectVersion = (nextId) => {
    if (nextId === currentVersionId) return;
    // Version Details (name/description) have their own dirty flag — they'd be
    // silently lost on switch too, so include them in the prompt guard.
    if (isDirty || versionDetailsDirty) {
      setPendingVersionSwitch(nextId);
      return;
    }
    doSelectVersion(nextId);
  };

  // Prompt actions for switching versions with unsaved edits.
  // Save & Switch: push the current version live, then switch.
  const confirmVersionSwitchSave = async () => {
    const nextId = pendingVersionSwitch;
    setPendingVersionSwitch(null);
    if (!nextId) return;
    const committed = await persist({ successMessage: "Product saved" });
    // If the save failed, stay on the current version so nothing is lost.
    if (!committed) return;
    doSelectVersion(nextId, committed);
  };
  // Switch Without Saving: the outgoing edits are kept with their version (they
  // are not pushed live), and we move on.
  const confirmVersionSwitchDiscard = () => {
    const nextId = pendingVersionSwitch;
    setPendingVersionSwitch(null);
    if (!nextId) return;
    doSelectVersion(nextId);
  };
  const cancelVersionSwitch = () => setPendingVersionSwitch(null);

  // Add a new version: a NEW COPY of whatever is currently on screen. The new
  // version starts as a draft (not live) — pushing it live is explicit (Save,
  // or "Set as live").
  const handleAddVersion = () => {
    const snapshot = buildSnapshot();
    const newId = `v-${Date.now()}`;
    // Commit the current version's edits first so we don't lose them, then add
    // the copy and switch to it.
    const committed = commitCurrent(versions);
    // "Version N" collides as soon as a version has been deleted, and duplicate
    // names are rejected on save — so walk N up until it's free.
    const taken = new Set(
      committed.map((v) =>
        String(v.name == null ? "" : v.name)
          .trim()
          .toLowerCase()
      )
    );
    let n = committed.length + 1;
    while (taken.has(`version ${n}`)) n += 1;
    const next = [
      ...committed,
      {
        id: newId,
        name: `Version ${n}`,
        active: false,
        description: "",
        content: snapshot,
      },
    ];
    setVersions(next);
    // The copy opens clean (its content === the form).
    setBaseline(snapshot);
    setCurrentVersionId(newId);
    saveVersions(next, newId);
  };

  // Version history / audit trail — shows a diff between the last saved
  // baseline and the current form state, with per-field Restore buttons.
  const handleShowHistory = () => setShowHistory(true);

  const handleRestoreField = React.useCallback((fieldKey, value) => {
    switch (fieldKey) {
      case "title":
        setTitle(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "author":
        setAuthor(value);
        break;
      case "category":
        setCategory(value);
        break;
      case "productType":
        setProductType(value);
        break;
      case "appCategories":
        setAppCategories(value);
        break;
      case "thumbnailUrl":
        setThumbnailUrl(value);
        break;
      case "productImages":
        setProductImages(value);
        break;
      case "publishStatus":
        setPublishStatus(value);
        break;
      case "availability":
        setAvailability(value);
        break;
      case "publishOnDate":
        setPublishOnDate(value);
        break;
      case "publishOnTime":
        setPublishOnTime(value);
        break;
      case "unpublishOnDate":
        setUnpublishOnDate(value);
        break;
      case "unpublishOnDateStr":
        setUnpublishOnDateStr(value);
        break;
      case "unpublishOnTimeStr":
        setUnpublishOnTimeStr(value);
        break;
      case "preOrderStart":
        setPreOrderStart(value || "");
        break;
      case "preOrderEnd":
        setPreOrderEnd(value || "");
        break;
      case "stock.currentStock":
        setStock((prev) => ({ ...prev, currentStock: value }));
        break;
      case "stock.vendorBin":
        setStock((prev) => ({ ...prev, vendorBin: value }));
        break;
      case "stock.caseQty":
        setStock((prev) => ({ ...prev, caseQty: value }));
        break;
      case "stock.onHand":
        setStock((prev) => ({ ...prev, onHand: value }));
        break;
      case "flags.source":
        setFlags((prev) => ({ ...prev, source: value }));
        break;
      case "flags.thirdPartyName":
        setFlags((prev) => ({ ...prev, thirdPartyName: value }));
        break;
      case "audienceGroupIds":
        setAudienceGroupIds(value || []);
        break;
      case "additionalIbos":
        setAdditionalIbos(value || []);
        break;
      case "pricing.retailCents":
        setPricing((prev) => ({ ...prev, retailCents: value }));
        break;
      case "pricing.premierMemberCents":
        setPricing((prev) => ({ ...prev, premierMemberCents: value }));
        break;
      case "pricing.lastPurchaseCents":
        setPricing((prev) => ({ ...prev, lastPurchaseCents: value }));
        break;
      case "flags.isNew":
        setFlags((prev) => ({ ...prev, isNew: value }));
        break;
      case "flags.markAsNewUntil":
        setFlags((prev) => ({ ...prev, markAsNewUntil: value }));
        break;
      case "flags.isReadable":
        setFlags((prev) => ({ ...prev, isReadable: value }));
        break;
      case "flags.isBundle":
        setFlags((prev) => ({ ...prev, isBundle: value }));
        break;
      case "bundlePricingMode":
        setBundlePricingMode(value);
        break;
      case "bundledProducts":
        setBundledProducts(value);
        break;
      case "pricing.bundledCents":
        setPricing((prev) => ({ ...prev, bundledCents: value }));
        break;
      case "pins":
        setPins(value);
        break;
      case "memberships":
        setMemberships(value);
        break;
      case "tagNames":
        setTagNames(value);
        break;
      default:
        break;
    }
  }, []);

  // "Set Live": make a version the single live one AND publish it to the store.
  // The store reads the BFF product record, so going live pushes the version's
  // content there (just like the main Save). When the target is the version
  // you're currently editing, any unsaved edits go live with it.
  const handleSetActiveVersion = async (id) => {
    // Work out the content that's going live and the committed version list.
    let goingLive;
    let committed;
    if (id === currentVersionId) {
      // Publish the current form (includes any unsaved edits).
      goingLive = buildSnapshot();
      committed = versions.map((v) =>
        v.id === id
          ? { ...v, content: goingLive, active: true }
          : { ...v, active: false }
      );
    } else {
      // Commit outgoing edits, then load + publish the target version's content.
      committed = commitCurrent(versions).map((v) => ({
        ...v,
        active: v.id === id,
      }));
      const target = committed.find((v) => v.id === id);
      goingLive = target && target.content ? target.content : buildSnapshot();
    }
    // Publish FIRST. Only commit the local/persisted version state once the
    // backend accepted the change — otherwise a failed PATCH (e.g. a 409
    // version conflict) would leave the version list claiming "live" while the
    // store record was never updated.
    try {
      await publishSnapshotToBff(goingLive);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't set this version live."));
      return;
    }
    if (id !== currentVersionId) {
      restoreSnapshot(goingLive);
      setCurrentVersionId(id);
    }
    setVersions(committed);
    setBaseline(goingLive);
    setServerPublished(goingLive.publishStatus === "published");
    saveVersions(committed, id);
    toast.success("This version is now live");
  };

  const handleDeleteVersion = (id) => {
    const target = versions.find((v) => v.id === id);
    if (!target || target.active || versions.length <= 1) return;
    const remaining = versions.filter((v) => v.id !== id);
    let nextCurrentId = currentVersionId;
    if (id === currentVersionId) {
      const fallback = remaining.find((v) => v.active) || remaining[0];
      const fallbackContent = fallback.content || buildSnapshot();
      restoreSnapshot(fallbackContent);
      setBaseline(fallbackContent);
      nextCurrentId = fallback.id;
      setCurrentVersionId(fallback.id);
    }
    setVersions(remaining);
    saveVersions(remaining, nextCurrentId);
  };

  const handleStatusChange = (nextStatus) => {
    if (nextStatus === publishStatus) return;
    // No per-click confirmation in the editor: nothing is persisted until the
    // user hits Save, and the Save confirmation already covers it. (The store
    // catalog's Archive button DOES confirm, since it archives immediately.)
    setPublishStatus(nextStatus);
  };

  // Delete the product (the version picker's trash, behind its confirmation).
  // Works at any status, so a clone made by mistake can go without being
  // published first. The backend soft-deletes (marks deletedAt and archives,
  // which takes it off the storefront); bringing it back needs backend help.
  const confirmDeleteProduct = async () => {
    if (!productId) return;
    try {
      await api.delete(productId);
      toast.success("Product deleted");
      // Bypass the unsaved-changes guard — we're intentionally leaving.
      allowLeaveRef.current = true;
      // Defer the redirect a tick so the toast can render before unmount.
      setTimeout(() => history.push("/store/home"), 50);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't delete the product."));
    }
  };

  const handleFlagsChange = (next) => {
    setFlags(next);
  };

  const handleSelectProductImages = (files) => {
    const fileList = Array.from(files);
    // Uploads can outlive a version switch; remember which version they were
    // started from so the images never land on the wrong version.
    const uploadVersionId = currentVersionIdRef.current;
    Promise.all(
      fileList.map((file) => mediaApi.upload(file).catch(() => null))
    ).then((assets) => {
      const next = assets.filter(Boolean);
      if (next.length === 0) {
        toast.error("All uploads failed. Please try again.");
        return;
      }
      if (next.length < fileList.length) {
        toast.error(
          `${fileList.length - next.length} file(s) failed to upload.`
        );
      }
      if (currentVersionIdRef.current === uploadVersionId) {
        setProductImages((prev) => [...prev, ...next]);
      } else {
        // The user switched versions mid-upload: append to the originating
        // version's stored content instead of the form on screen.
        setVersions((prev) =>
          prev.map((v) =>
            v.id === uploadVersionId && v.content
              ? {
                  ...v,
                  content: {
                    ...v.content,
                    productImages: [
                      ...(v.content.productImages || []),
                      ...next,
                    ],
                  },
                }
              : v
          )
        );
        toast.success("Image(s) added to the version they were uploaded from");
      }
    });
  };

  return (
    <div className={styles.editor}>
      <Toaster position="top-right" />

      {showHistory && baseline && (
        <HistoryOverlay
          baseline={baseline}
          current={buildSnapshot()}
          onRestoreField={handleRestoreField}
          onClose={() => setShowHistory(false)}
          onUndoAll={() => {
            setShowHistory(false);
            setPendingUndo(true);
          }}
        />
      )}

      {loadError && (
        <div className={styles.loadError} role="alert">
          {errorMessage(loadError, "Couldn't load this product.")}
        </div>
      )}

      <div className={styles.actionBar}>
        <div className={styles.actionBarLeft}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToStore}
            disabled={saving}
            title="Return to the product list. Your progress is auto-saved."
          >
            <ChevronLeft size={18} />
            <span className={styles.backLabel}>Back</span>
          </button>
          {(isDirty || versionDetailsDirty) && (
            <span className={styles.dirtyBadge}>● Unsaved</span>
          )}
        </div>

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.historyButton}
            onClick={handleShowHistory}
            disabled={saving}
            title="View the change history for this product."
          >
            <History size={16} />
            <span>History</span>
          </button>
          <VersionPicker
            versions={versions}
            currentVersionId={currentVersionId}
            onSelect={handleSelectVersion}
            onAdd={handleAddVersion}
            onSetActive={handleSetActiveVersion}
            onDelete={handleDeleteVersion}
            onDeleteProduct={
              isEdit && productId && serverProduct
                ? confirmDeleteProduct
                : undefined
            }
            productName={title}
          />
          {liveStateKnown && (
            <Button
              purpose={isLive ? ButtonPurpose.generic : ButtonPurpose.save}
              onClick={handleSave}
              disabled={
                (!isDirty && !versionDetailsDirty) ||
                saving ||
                loading ||
                Boolean(loadError)
              }
              title="Save this version at its current Publish Status. If this is the live version, the changes also publish to the store."
            >
              {saving ? "Saving…" : isLive ? "Push Revisions" : "Push Live"}
            </Button>
          )}
        </div>
      </div>

      {loading && <div className={styles.loadingBanner}>Loading product…</div>}

      {currentVersion && (
        <VersionDetailsCard
          key={currentVersion.id}
          currentVersion={currentVersion}
          onDirtyChange={setVersionDetailsDirty}
          onChange={handleVersionDetailsChange}
        />
      )}

      <div className={styles.grid}>
        <div className={styles.leftColumn}>
          <CollapsibleSection title="Netsuite Info">
            <div className={styles.netsuiteRow}>
              <span className={styles.netsuiteLabel}>SKU:</span>
              <span className={styles.netsuiteValue}>{sku || "—"}</span>
            </div>
            <div className={styles.netsuiteRow}>
              <span className={styles.netsuiteLabel}>Netsuite Title:</span>
              <span className={styles.netsuiteValue}>
                {netsuiteTitle || "—"}
              </span>
            </div>

            <div className={styles.sectionHeading}>Product</div>

            <label className={styles.label} htmlFor="pe-product-name">
              Product Name
            </label>
            <input
              id="pe-product-name"
              className={styles.input}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
              placeholder="Product name"
            />
            <label className={styles.label} htmlFor="pe-author">
              Author(s)
            </label>
            <div className={styles.autocompleteWrap}>
              <input
                id="pe-author"
                className={styles.input}
                value={author}
                onChange={(e) => {
                  setAuthor(e.target.value);
                  setShowAuthorDropdown(true);
                }}
                onFocus={() => setShowAuthorDropdown(true)}
                onBlur={() =>
                  setTimeout(() => setShowAuthorDropdown(false), 200)
                }
                placeholder="Search authors..."
                autoComplete="off"
              />
              {showAuthorDropdown && authorSuggestions.length > 0 && (
                <ul className={styles.autocompleteDropdown}>
                  {authorSuggestions.map((name) => (
                    <li
                      key={name}
                      className={styles.autocompleteItem}
                      onMouseDown={() => {
                        setAuthor(name);
                        setShowAuthorDropdown(false);
                      }}
                    >
                      {name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <label className={styles.label} htmlFor="pe-description">
              Description
            </label>
            <textarea
              id="pe-description"
              className={styles.textarea}
              rows={3}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
              }}
              placeholder="Description"
            />

            <div className={styles.sectionHeading}>General Information</div>
            {[
              { key: "isNew", label: "New!" },
              { key: "isReadable", label: "Readable" },
              { key: "isBundle", label: "Bundle" },
            ].map((f) => (
              <div key={f.key} className={styles.flagRow}>
                <span className={styles.flagLabel}>{f.label}</span>
                <div className={styles.yesNoGroup}>
                  <button
                    type="button"
                    className={`${styles.yesNoBtn} ${
                      flags[f.key] ? styles.yesNoBtnYesActive : ""
                    }`}
                    onClick={() => {
                      handleFlagsChange({ ...flags, [f.key]: true });
                    }}
                    title={`Set ${f.label} to Yes.`}
                  >
                    <span className={styles.yesNoCheckbox}>
                      {flags[f.key] && (
                        <svg
                          viewBox="0 0 14 14"
                          width="10"
                          height="10"
                          aria-hidden="true"
                        >
                          <path
                            d="M2 7 L6 11 L12 3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    Yes
                  </button>
                  <button
                    type="button"
                    className={`${styles.yesNoBtn} ${
                      !flags[f.key] ? styles.yesNoBtnNoActive : ""
                    }`}
                    onClick={() => {
                      handleFlagsChange({ ...flags, [f.key]: false });
                    }}
                    title={`Set ${f.label} to No.`}
                  >
                    <span className={styles.yesNoCheckbox}>
                      {!flags[f.key] && (
                        <svg
                          viewBox="0 0 14 14"
                          width="10"
                          height="10"
                          aria-hidden="true"
                        >
                          <path
                            d="M2 7 L6 11 L12 3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </span>
                    No
                  </button>
                </div>
                {f.key === "isNew" &&
                  flags.isNew &&
                  (() => {
                    const markAsNewDate =
                      flags.markAsNewUntil &&
                      !Number.isNaN(new Date(flags.markAsNewUntil).getTime())
                        ? new Date(flags.markAsNewUntil)
                        : null;
                    const dateValue = markAsNewDate
                      ? toDateInputValue(markAsNewDate)
                      : "";
                    const timeValue = markAsNewDate
                      ? toTimeInputValue(markAsNewDate)
                      : "23:59";
                    return (
                      <div className={styles.markAsNewBlock}>
                        <label
                          className={styles.markAsNewLabel}
                          htmlFor="pe-mark-as-new-until"
                        >
                          Mark as New Until
                        </label>
                        <div className={styles.dateInputs}>
                          <div className={styles.iconInput}>
                            <input
                              id="pe-mark-as-new-until"
                              type="date"
                              className={styles.input}
                              value={dateValue}
                              onChange={(e) => {
                                const dateStr = e.target.value;
                                const existingTime = markAsNewDate
                                  ? toTimeInputValue(markAsNewDate)
                                  : "23:59";
                                handleFlagsChange({
                                  ...flags,
                                  markAsNewUntil: dateStr
                                    ? new Date(
                                        `${dateStr}T${existingTime}`
                                      ).toISOString()
                                    : null,
                                });
                              }}
                            />
                          </div>
                          <div className={styles.iconInput}>
                            <input
                              type="time"
                              className={styles.input}
                              value={timeValue}
                              onChange={(e) => {
                                const timeStr = e.target.value || "23:59";
                                const existingDate = markAsNewDate
                                  ? toDateInputValue(markAsNewDate)
                                  : new Date().toISOString().slice(0, 10);
                                handleFlagsChange({
                                  ...flags,
                                  markAsNewUntil: new Date(
                                    `${existingDate}T${timeStr}`
                                  ).toISOString(),
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })()}
              </div>
            ))}
          </CollapsibleSection>

          <CollapsibleSection title="Publish">
            <div className={styles.publishRow}>
              <span className={styles.label}>Publish Status</span>
              <div
                className={`${styles.statusButtons} ${styles.statusButtonsRight}`}
              >
                {PUBLISH_STATUSES.map((status) => {
                  const isActive = publishStatus === status.key;
                  return (
                    <button
                      key={status.key}
                      type="button"
                      className={`${styles.statusButton} ${
                        isActive
                          ? styles[status.activeClass]
                          : styles[status.baseClass]
                      }`}
                      onClick={() => handleStatusChange(status.key)}
                      title={`Set the publish status to ${status.label}.`}
                    >
                      {status.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.dateRow}>
              <div className={styles.dateColumn}>
                <label className={styles.label} htmlFor="pe-publish-on">
                  Publish On
                </label>
                <div className={styles.dateInputs}>
                  <div className={styles.iconInput}>
                    <input
                      id="pe-publish-on"
                      type="date"
                      className={styles.input}
                      placeholder="Date"
                      value={publishOnDate}
                      onChange={(e) => {
                        setPublishOnDate(e.target.value);
                      }}
                    />
                  </div>
                  <div className={styles.iconInput}>
                    <input
                      type="time"
                      className={styles.input}
                      placeholder="Time"
                      value={publishOnTime}
                      onChange={(e) => {
                        setPublishOnTime(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.dateColumn}>
                <label className={styles.label} htmlFor="pe-unpublish-on">
                  Unpublish On
                </label>
                <div className={styles.dateInputs}>
                  <div className={styles.iconInput}>
                    <input
                      id="pe-unpublish-on"
                      type="date"
                      className={styles.input}
                      placeholder="Date"
                      value={unpublishOnDateStr}
                      onChange={(e) => {
                        setUnpublishOnDateStr(e.target.value);
                      }}
                    />
                  </div>
                  <div className={styles.iconInput}>
                    <input
                      type="time"
                      className={styles.input}
                      placeholder="Time"
                      value={unpublishOnTimeStr}
                      onChange={(e) => {
                        setUnpublishOnTimeStr(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <label className={styles.inlineCheckbox}>
                  <input
                    type="checkbox"
                    checked={unpublishOnDate}
                    onChange={(e) => {
                      setUnpublishOnDate(e.target.checked);
                    }}
                    className={styles.checkboxInput}
                  />
                  <span className={styles.checkboxBox} />
                  Unpublish on this date
                </label>
              </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.flagRow}>
              <span className={styles.flagLabel}>Source</span>
              <div className={styles.yesNoGroup}>
                <button
                  type="button"
                  className={`${styles.yesNoBtn} ${
                    flags.source !== "third_party"
                      ? styles.yesNoBtnYesActive
                      : ""
                  }`}
                  onClick={() => {
                    handleFlagsChange({ ...flags, source: "wwg" });
                  }}
                  title="Mark this product as WWG Exclusive."
                >
                  <span className={styles.yesNoCheckbox}>
                    {flags.source !== "third_party" && (
                      <svg
                        viewBox="0 0 14 14"
                        width="10"
                        height="10"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 7 L6 11 L12 3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  WWG
                </button>
                <button
                  type="button"
                  className={`${styles.yesNoBtn} ${
                    flags.source === "third_party"
                      ? styles.yesNoBtnNoActive
                      : ""
                  }`}
                  onClick={() => {
                    handleFlagsChange({ ...flags, source: "third_party" });
                  }}
                  title="Mark this product as fulfilled by a 3rd party."
                >
                  <span className={styles.yesNoCheckbox}>
                    {flags.source === "third_party" && (
                      <svg
                        viewBox="0 0 14 14"
                        width="10"
                        height="10"
                        aria-hidden="true"
                      >
                        <path
                          d="M2 7 L6 11 L12 3"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  3rd Party
                </button>
              </div>
            </div>
            {flags.source === "third_party" && (
              <div className={styles.subRow}>
                <label className={styles.label} htmlFor="pe-third-party-name">
                  3rd Party Name
                </label>
                <input
                  id="pe-third-party-name"
                  type="text"
                  className={styles.input}
                  value={flags.thirdPartyName || ""}
                  onChange={(e) =>
                    handleFlagsChange({
                      ...flags,
                      thirdPartyName: e.target.value,
                    })
                  }
                />
              </div>
            )}
          </CollapsibleSection>

          <ContentSettingsCard
            membershipOptions={membershipOptions}
            selectedMemberships={memberships}
            onMembershipChange={(v) => {
              setMemberships(v);
            }}
            pinLevelOptions={pinLevels}
            selectedPinLevels={pins}
            onPinLevelChange={(v) => {
              setPins(v);
            }}
            selectedFamilyGroupIds={audienceGroupIds}
            onFamilyGroupsChange={(v) => {
              setAudienceGroupIds(v);
            }}
            selectedDiamondIds={audienceGroupIds}
            onDiamondsChange={(v) => {
              setAudienceGroupIds(v);
            }}
            additionalIbos={additionalIbos}
            onAdditionalIbosChange={(v) => {
              setAdditionalIbos(v);
            }}
            selectedTagIds={tagNames}
            onTagsChange={(v) => {
              setTagNames(v);
            }}
            selectedAppCategories={appCategories}
            onAppCategoriesChange={(v) => {
              setAppCategories(v);
            }}
          />
        </div>

        <div className={styles.rightColumn}>
          <ThumbnailCard
            title="Thumbnail Image"
            value={thumbnailUrl}
            netsuiteUrl={netsuiteThumbnailUrl}
            onChange={(asset) => {
              // ThumbnailCard invokes the onChange closure it captured when the
              // upload STARTED, so `currentVersionId` here is the version the
              // file was picked on. Route the result to that version if the
              // user has since switched away.
              if (currentVersionIdRef.current === currentVersionId) {
                setThumbnailUrl(asset);
              } else {
                setVersions((prev) =>
                  prev.map((v) =>
                    v.id === currentVersionId && v.content
                      ? { ...v, content: { ...v.content, thumbnailUrl: asset } }
                      : v
                  )
                );
                toast.success(
                  "Thumbnail applied to the version it was uploaded from"
                );
              }
            }}
            imageAlt={title || "Product thumbnail"}
            actionLabel="Select Thumbnail"
          />

          <ProductImageCard
            images={productImages}
            onSelectFile={handleSelectProductImages}
            onChange={(next) => {
              setProductImages(next);
            }}
          />

          {/* Pricing saves through the normal product PATCH — `pricing` is part
              of the snapshot payload (validated in the BFF's sanitizeProductWrite)
              and publishes to SQL: retail onto Store.Products, premierMember onto
              the baseline variation's PREMIER_MEMBER price level. lastPurchase is
              informational and deliberately not published. There is no separate
              setPricing endpoint. */}
          {flags.isBundle ? (
            <BundlePricingCard
              pricing={pricing}
              bundledProducts={bundledProducts}
              bundlePricingMode={bundlePricingMode}
              onPricingChange={(next) => {
                setPricing(next);
              }}
              onBundleModeChange={setBundlePricingMode}
              onBundledProductsChange={setBundledProducts}
            />
          ) : (
            <PricingCard
              pricing={pricing}
              netsuitePricing={netsuitePricing}
              onChange={(next) => {
                setPricing(next);
              }}
            />
          )}

          <CollapsibleSection title="Availability">
            <div className={styles.cardBody}>
              <div className={styles.publishRow}>
                {/* The deck labels this row "Status" inside an "Availability"
                    card; repeating "Availability" twice read as a mistake. */}
                <span className={styles.label}>Status</span>
                <div className={styles.statusButtons}>
                  {/* "Discontinued" was retired per BugHerd #73 — a product we
                      stop selling is Archived instead. Legacy records may still
                      carry the value, so the catalog filter still offers it. */}
                  {[
                    { key: "preorder", label: "Pre-Order" },
                    { key: "available", label: "Available" },
                    { key: "outofstock", label: "Out of Stock" },
                  ].map((status) => {
                    // Pre-order has no storefront support yet: there is no
                    // pre-order column in SQL, so nothing downstream can read
                    // it. Shown but not selectable until Rev 2 (see the Sep 8
                    // thread with Kevin/Patrick). A product already carrying the
                    // value can still be moved off it.
                    const notImplemented =
                      status.key === "preorder" && availability !== "preorder";
                    return (
                      <button
                        key={status.key}
                        type="button"
                        disabled={notImplemented}
                        className={`${styles.statusButton} ${
                          availability === status.key
                            ? status.key === "available"
                              ? styles.availabilityAvailableActive
                              : status.key === "preorder"
                              ? styles.availabilityPreorderActive
                              : styles.availabilityOutOfStockActive
                            : status.key === "available"
                            ? styles.availabilityAvailable
                            : status.key === "preorder"
                            ? styles.availabilityPreorder
                            : styles.availabilityOutOfStock
                        }${
                          notImplemented
                            ? ` ${styles.statusButtonDisabled}`
                            : ""
                        }`}
                        onClick={() => {
                          if (notImplemented) return;
                          setAvailability(status.key);
                        }}
                        title={
                          notImplemented
                            ? "Pre-Order isn't implemented yet — it's scheduled for Rev 2. The storefront has no pre-order field to read, so setting it here would do nothing."
                            : `Set availability to ${status.label}.`
                        }
                      >
                        {status.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {availability === "preorder" && (
                <div className={styles.publishRow}>
                  <div className={styles.dateInputs}>
                    <div className={styles.iconInput}>
                      <label className={styles.label}>Start Date</label>
                      <input
                        type="date"
                        className={styles.input}
                        value={preOrderStart}
                        onChange={(e) => {
                          setPreOrderStart(e.target.value);
                        }}
                      />
                    </div>
                    <div className={styles.iconInput}>
                      <label className={styles.label}>End Date</label>
                      <input
                        type="date"
                        className={styles.input}
                        value={preOrderEnd}
                        onChange={(e) => {
                          setPreOrderEnd(e.target.value);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* A bundle has no stock of its own: what can be sold is decided by
              the component that runs out first (Wrike "[P1] Stock/inventory
              for bundles"). Figures are as of the last save or load. */}
          {flags.isBundle && (
            <CollapsibleSection title="Stock">
              <div className={styles.stockSection}>
                <div className={styles.stockRow}>
                  <span className={styles.stockRowLabel}>Bundle</span>
                  <div className={styles.stockPills}>
                    <div className={styles.stockPill}>
                      <span className={styles.stockPillLabel}>
                        Available Bundles
                      </span>
                      <span className={styles.stockPillDivider} />
                      <span className={styles.stockPillReadonly}>
                        {bundleStock && bundleStock.available != null
                          ? bundleStock.available.toLocaleString()
                          : "—"}
                      </span>
                    </div>
                    <span className={styles.stockNetsuiteHint}>
                      lowest stock of the products inside
                    </span>
                  </div>
                </div>
                {bundleStock && bundleStock.components.length > 0 && (
                  <ul className={styles.bundleStockList}>
                    {bundleStock.components.map((c) => (
                      <li
                        key={c.sku}
                        className={
                          c.sku === bundleStock.limitedBy
                            ? styles.bundleStockLimit
                            : undefined
                        }
                      >
                        <span>
                          {c.title}
                          {c.perBundle > 1 ? ` ×${c.perBundle}` : ""}
                        </span>
                        <span>
                          {c.available == null
                            ? "Not tracked"
                            : `${c.available.toLocaleString()} in stock`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CollapsibleSection>
          )}

          {/* A bundle has no NetSuite variations of its own, and its stock is
              shown above from its components. */}
          {!flags.isBundle && (
            <>
              <CollapsibleSection title="Stock">
                <div className={styles.stockSection}>
                  <div className={styles.stockRow}>
                    <span className={styles.stockRowLabel}>Universal</span>
                    <div className={styles.stockPills}>
                      {/*
                    Current Stock is owned by NetSuite — it's the live on-hand
                    count synced from inventory, so it's read-only here. The old
                    admin "+ N units" adjustment has been removed: stock is not
                    set from the Store Admin. (Admins can still flag an item
                    "Out of stock" manually via the Availability control above.)
                  */}
                      <div className={styles.stockPill}>
                        <span className={styles.stockPillLabel}>
                          Current Stock
                        </span>
                        <span className={styles.stockPillDivider} />
                        <span className={styles.stockPillReadonly}>
                          {netsuiteStock && netsuiteStock.currentStock != null
                            ? netsuiteStock.currentStock
                            : "—"}
                        </span>
                      </div>
                      <span className={styles.stockNetsuiteHint}>
                        from NetSuite
                      </span>
                    </div>
                  </div>

                  <div className={styles.stockVendorLabel}>Vendor</div>
                  <div className={styles.sectionDivider} />

                  <div className={styles.stockRow}>
                    <div className={styles.stockPills}>
                      {[
                        { key: "vendorBin", label: "Bin" },
                        { key: "caseQty", label: "Case QTY" },
                        { key: "onHand", label: "On Hand" },
                      ].map(({ key, label }) => {
                        const base = netsuiteStock ? netsuiteStock[key] : null;
                        const overridden =
                          String(stock[key] ?? "").trim() !== "";
                        return (
                          <div className={styles.stockPillGroup} key={key}>
                            <div className={styles.stockPill}>
                              <span className={styles.stockPillLabel}>
                                {label}
                              </span>
                              <span className={styles.stockPillDivider} />
                              <input
                                className={styles.stockPillInput}
                                placeholder={
                                  base != null ? String(base) : "N/A"
                                }
                                value={stock[key]}
                                onChange={(e) => {
                                  setStock((s) => ({
                                    ...s,
                                    [key]: e.target.value,
                                  }));
                                }}
                              />
                            </div>
                            {overridden && (
                              <div className={styles.stockSourceRow}>
                                <button
                                  type="button"
                                  className={styles.stockResetButton}
                                  onClick={() => {
                                    setStock((s) => ({ ...s, [key]: "" }));
                                  }}
                                >
                                  Reset
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Variations">
                {/* The store's own colours/sizes when the product is in SQL;
                    otherwise the read-only NetSuite summary. */}
                {/* Only when there's something to edit: a single-item product
                    (a book) has one variation and no sizes. */}
                {Array.isArray(serverProduct?.storeVariations) &&
                (serverProduct.storeVariations.length > 1 ||
                  serverProduct.storeVariations.some(
                    (v) => (v.variants || []).length > 0
                  )) ? (
                  <StoreVariationsCard
                    productId={serverProduct.id}
                    variations={serverProduct.storeVariations}
                    onSaved={(updated) =>
                      setServerProduct((prev) =>
                        prev
                          ? {
                              ...prev,
                              storeVariations:
                                updated?.storeVariations ??
                                prev.storeVariations,
                            }
                          : prev
                      )
                    }
                  />
                ) : (
                  <NetSuiteVariationsCard
                    variations={serverProduct && serverProduct.variations}
                  />
                )}
              </CollapsibleSection>
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={pendingUndo}
        title="Undo all changes?"
        onClose={() => setPendingUndo(false)}
      >
        <div className={styles.archiveModalBody}>
          <p>
            This reverts every field back to the last saved version. Any edits
            you have made since then will be discarded and cannot be recovered.
          </p>
          <div className={styles.archiveModalActions}>
            <Button
              onClick={() => setPendingUndo(false)}
              title="Keep your current changes and close this dialog."
            >
              Keep Editing
            </Button>
            <Button
              purpose={ButtonPurpose.delete}
              onClick={confirmUndo}
              title="Revert every field to the last saved version. Your unsaved edits will be discarded and cannot be recovered."
            >
              Undo Changes
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={pendingBack}
        title="Leave the editor?"
        onClose={() => setPendingBack(false)}
      >
        <div className={styles.archiveModalBody}>
          <p>
            Your progress is auto-saved. You can leave and come back anytime to
            pick up right where you left off.
          </p>
          {versionDetailsDirty && (
            <p>
              <strong>Note:</strong> Your unsaved{" "}
              <strong>Version Details</strong> (name/description) are not
              auto-saved. Click <strong>Save</strong> before leaving or they
              will be lost.
            </p>
          )}
          <p>
            Click <strong>Save</strong> to store your changes. If this is the
            live version, they also publish to the store.
          </p>
          <div className={styles.archiveModalActions}>
            <Button
              onClick={() => setPendingBack(false)}
              title="Stay in the editor and continue editing."
            >
              Keep Editing
            </Button>
            <Button
              purpose={ButtonPurpose.delete}
              onClick={doBackToStore}
              title="Return to the product list. Your progress is auto-saved but not yet published."
            >
              Leave
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={pendingSave}
        title="Save changes?"
        onClose={() => setPendingSave(false)}
      >
        <div className={styles.archiveModalBody}>
          <p>
            Saving overwrites the stored product with your current changes. This
            becomes the new saved version and cannot be undone afterward.
          </p>
          {publishStatus === "scheduled" &&
            !parsePublishAt(publishOnDate, publishOnTime) && (
              <p>
                <strong>
                  Status is Scheduled with no Publish On date, so this product
                  won't go live until you add one.
                </strong>
              </p>
            )}
          {publishStatus === "published" &&
            baseline &&
            baseline.publishStatus !== "published" && (
              <p>
                <strong>
                  This publishes the product: members can see it as soon as you
                  save.
                </strong>
              </p>
            )}
          <div className={styles.archiveModalActions}>
            <Button
              onClick={() => setPendingSave(false)}
              title="Don't save and close this dialog."
            >
              Cancel
            </Button>
            <Button
              purpose={ButtonPurpose.save}
              onClick={confirmSave}
              title="Overwrite the stored product with your current changes. This becomes the new saved version."
            >
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={pendingVersionSwitch !== null}
        title="Switch versions?"
        onClose={cancelVersionSwitch}
      >
        <div className={styles.archiveModalBody}>
          <p>
            Your progress is auto-saved to this version, but won't go live until
            you click <strong>Save</strong> on the live version. Switch now and
            come back later — nothing will be lost.
          </p>
          <div className={styles.archiveModalActions}>
            <Button
              onClick={cancelVersionSwitch}
              title="Stay on the current version and close this dialog."
            >
              Cancel
            </Button>
            <Button
              onClick={confirmVersionSwitchDiscard}
              title="Switch to the other version without saving your current edits."
            >
              Switch Without Saving
            </Button>
            <Button
              purpose={ButtonPurpose.save}
              onClick={confirmVersionSwitchSave}
              title="Save your current edits, then switch to the other version."
            >
              Save &amp; Switch
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

StoreProductEditor.propTypes = {
  mode: PropTypes.oneOf(["add", "edit"]).isRequired,
};

export default StoreProductEditor;
